// outbox-refs.js — letting a queued change point at a record that does not
// exist yet.
// ---------------------------------------------------------------------------
// 2026-08-20. Brett did a 25-pin inspection on a site with no reception. The
// defects synced. Every photo was gone — 25 pins, zero photos, and nothing on
// screen that had ever said so.
//
// THE CAUSE. Creating a defect offline queues the POST and returns a marker,
// not a saved defect, so there is no defect id yet. The photo upload was
// guarded on that id:
//
//     const created = defectFromApi(await apiSend('POST', '/api/defects', …));
//     if (Array.isArray(formPhotos) && created?.id) {   // ← false when offline
//       … addToOutbox('POST', '/api/defect-photos', { defectId: created.id, … })
//     }
//
// With no id the guard was false, the loop never ran, and the photos — which
// only ever existed in that screen's form state, because the outbox is the
// app's only durable local store — were dropped on the floor. The defect
// replayed fine hours later, which is precisely what made it invisible: the
// work looked like it had synced.
//
// THE FIX. A queued change can now name a record that has not been created
// yet. The photo is queued with `defectId: ref(localId)` — a placeholder
// pointing at the queued defect — and when the drain creates that defect and
// the server hands back a real id, the placeholder is replaced before the
// photo is sent.
//
// WHAT THIS MUST NEVER DO is send a payload with an unresolved placeholder in
// it. A photo posted against a made-up defect id is worse than one that failed:
// it either 400s (and gets dropped as "rejected") or, on a less careful
// endpoint, attaches somebody's kitchen photo to an unrelated defect. So an
// unresolved reference HOLDS the item in the queue instead, where the sync
// banner can show it.

/** The marker shape. Deliberately verbose — it can end up in IndexedDB for days. */
const REF_KEY = '__outboxRef';

/** A placeholder for the id of a record queued earlier in this outbox. */
export function ref(localId) {
  return { [REF_KEY]: localId };
}

/** Is this value a placeholder? */
export function isRef(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v) && Object.prototype.hasOwnProperty.call(v, REF_KEY);
}

/**
 * Replace every placeholder in a queued body with the real id it now refers to.
 *
 * @param {any} body        the queued request body
 * @param {Map} idMap       localId → server id, built as the drain succeeds
 * @returns {{body: any, unresolved: Array}} the rewritten body, and the local
 *          ids that could not be resolved. A non-empty `unresolved` means DO
 *          NOT SEND — see the note above.
 */
export function resolveRefs(body, idMap) {
  const unresolved = [];
  const map = idMap instanceof Map ? idMap : new Map(Object.entries(idMap || {}));
  // IndexedDB hands keys back as numbers; object keys arrive as strings. Both
  // forms have to match, or every photo would come back unresolved — which
  // looks exactly like the bug this file exists to fix.
  const get = (k) => {
    if (map.has(k)) return map.get(k);
    if (map.has(String(k))) return map.get(String(k));
    const n = Number(k);
    return Number.isNaN(n) ? undefined : map.get(n);
  };

  const walk = (v) => {
    if (isRef(v)) {
      const localId = v[REF_KEY];
      const real = get(localId);
      if (real == null) { unresolved.push(localId); return v; }
      return real;
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const out = {};
      for (const [k, val] of Object.entries(v)) out[k] = walk(val);
      return out;
    }
    return v;
  };

  return { body: walk(body), unresolved };
}

/**
 * The id the server just handed back for a created record, or null.
 *
 * Only a CREATE can be pointed at, so only a POST is worth capturing. The
 * shapes are the two this API actually returns — `id` from the DropPin
 * endpoints, `Id` defensively — and anything else is simply not referenceable
 * rather than a guess.
 */
export function createdId(method, responseBody) {
  if (String(method || '').toUpperCase() !== 'POST') return null;
  const b = responseBody;
  if (!b || typeof b !== 'object') return null;
  const id = b.id ?? b.Id;
  return (typeof id === 'number' || typeof id === 'string') ? id : null;
}
